<script lang="ts" setup>
import { proximityScoreMap } from '@thecompaniesapi/shared/client'

const props = defineProps({
  attributesToExclude: {
    default: () => [],
    type: Array as PropType<SegmentationAttribute[]>,
  },
  companies: {
    default: undefined,
    type: Array as PropType<CompanyV2[]>,
  },
  confirmOnChange: {
    default: false,
    type: Boolean,
  },
  hasTooltip: {
    default: false,
    type: Boolean,
  },
  modelValue: {
    required: true,
    type: Object as PropType<SegmentationCondition>,
  },
})

const emit = defineEmits<{
  'confirm': []
  'update:modelValue': [condition: SegmentationCondition]
}>()

const { t } = useI18n()

const opened = ref(false)
const localValue = ref<SegmentationCondition>({ ...props.modelValue })

const isDifferent = computed(() => {
  return JSON.stringify(localValue?.value || {}) !== JSON.stringify(props?.modelValue || {})
})

const tooltip = computed(() => {
  let tooltip = ''

  tooltip += props.modelValue.sign === 'notEquals' ? '(≠)' : '(=)'
  tooltip += ' '

  // Add value to tooltip until it reaches 75 characters
  let ellipsisToAdd = false

  // Handle AI search case that is specific
  if (props.modelValue.attribute === 'ai.search' && typeof props.modelValue.values?.[0] === 'string' && props.modelValue.values[0].length) {
    tooltip += props.modelValue.values?.[0]?.slice?.(0, 20)

    if (props.modelValue.values[0].length > 60) {
      tooltip += '...'
    }

    if (props.modelValue.values[1]) {
      let scoreTooltip = ''

      const score = Number.parseFloat(String(props.modelValue.values[1]))

      if (score >= proximityScoreMap.close) {
        scoreTooltip = `(${t('globals.similarityHigh')})`
      }
      else if (score >= proximityScoreMap.near) {
        scoreTooltip = `(${t('globals.similarityMedium')})`
      }
      else if (score >= proximityScoreMap.far) {
        scoreTooltip = `(${t('globals.similarityLow')})`
      }

      tooltip += ` ${scoreTooltip}`
    }

    return tooltip
  }
  // Handle other segmentation attributes
  else {
    for (const value of props.modelValue.values) {
      if (tooltip.length + String(value).length > 75) {
        ellipsisToAdd = true
        break
      }

      tooltip += changeCase.capitalCase(`${String(value).replaceAll('-', ' ')}`)
      tooltip += props.modelValue.operator === 'or' ? ' or ' : ' and '
    }
  }

  // Remove last operator if it exists and add ellipsis if too long
  tooltip = tooltip.replace(/ (or|and) $/, '')

  if (ellipsisToAdd) {
    tooltip += '...'
  }

  return tooltip
})

function onConditionConfirmed(condition: SegmentationCondition) {
  // Update local value
  localValue.value = condition

  // Emit update
  emit('update:modelValue', localValue.value)

  // Wait for modelValue to be updated
  nextTick(() => {
    emit('confirm')

    opened.value = false
  })
}

watch(
  opened,
  (newVal) => {
    if (newVal) {
      localValue.value = { ...props.modelValue }
    }
  },
)

function onConditionUpdated(condition: SegmentationCondition) {
  localValue.value = condition

  if (props.confirmOnChange) {
    emit('update:modelValue', localValue.value)
  }
}

defineExpose({ opened })
</script>

<template>
  <UPopover
    v-model:open="opened"
    class="flex-initial flex-shrink-0 select-none"
    :popper="{ placement: 'bottom-start' }"
    :ui="{ base: 'overflow-visible', container: 'sm:w-120 w-full pr-6 pl-2 sm:px-0' }"
  >
    <UTooltip
      :popper="{ placement: 'bottom' }"
      :prevent="!hasTooltip"
      :text="tooltip"
    >
      <slot :condition="localValue" />
    </UTooltip>

    <template #panel>
      <div class="mt-1 flex max-w-full flex-col gap-4 p-4 sm:max-w-lg md:mr-0">
        <SegmentationCondition
          :attribute="localValue.attribute"
          :attributes-to-exclude="attributesToExclude"
          :blocked-operator="localValue.blockedOperator"
          :companies="companies"
          :is-removable="false"
          :operator="localValue.operator"
          :sign="localValue.sign"
          :values="localValue.values"
          @condition-confirmed="onConditionConfirmed"
          @condition-updated="onConditionUpdated"
        />

        <slot
          :condition="localValue"
          :confirm-condition="() => onConditionConfirmed(localValue)"
          :is-different="isDifferent"
          name="actions"
        />
      </div>
    </template>
  </UPopover>
</template>
